import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-responsive-modal";
import Scrollbar from "react-scrollbars-custom";
import { useToasts } from "react-toast-notifications";
import Button from "components/CustomButton";
import Loader from "components/Loader";
import DefaultTeamAvatar from "assets/img/HandsColorAvatar.jpeg";
import CourseService from "services/CourseService";
import { ReactComponent as ShareIcon } from "assets/img/icons/share-list.svg";

const styles = {
  headingDailog: {
    fontWeight: "600",
  },
  listIteamHeading: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "10px",
    gap: "15px",
    borderBottom: "1px solid #ccc",
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    borderBottom: "1px solid #ccc",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  selectedListItem: {
    backgroundColor: "#fff8e6",
  },
  teamDetail: {
    display: "flex",
    gap: "10px",
    fontWeight: "400",
    alignItems: "center",
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
  },
  dialogContent: {
    height: "450px",
    maxHeight: "450px",
    overflow: "auto",
    flex: "1 1 auto",
    padding: "8px 24px",
  },
  avatar: {
    minHeight: "30px",
    height: "30px",
    minWidth: "30px",
    width: "30px",
    borderRadius: "50%",
  },
  teamName: {
    fontWeight: "400",
  },
  rightButton: {
    position: "absolute",
    top: "0",
    right: "0",
  },
  noTeams: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  shareButton: {
    cursor: "pointer",
  },
  loaderClass: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  dialogTitle: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "0 0 auto",
    margin: 0,
    padding: "12px 0px 12px",
  },
  doneButton: {
    width: "100px",
    background: "none",
    height: "unset",
    color: "#ffa100",
    boxShadow: "unset",
  },
  statusErrorMessage: {
    color: "red",
    fontSize: "14px",
    marginTop: "10px",
  }
};

export default ({ course, teamId, renderButton, onAssignmentComplete }) => {
  const classes = styles;
  const [dialogState, setDialogState] = useState(false);
  const { addToast } = useToasts();
  const [allTeams, setAllTeams] = useState();
  const [shareTeams, setSharedTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingTeams, setFetchingTeams] = useState(false);
  const [showStatusError, setShowStatusError] = useState(false);
  
  const fetchTeams = useCallback(async () => {
    try {
      setFetchingTeams(true);
      const response = await CourseService.getCourseSharingTeams();
      
      if (response && response.data && response.data.data) {
        const filteredTeams = response.data.data.filter(team => {
          const isCurrentTeam = team.id === Number(teamId);
          const isAlreadyAssigned = course?.assigned_teams?.some(
            assignedTeam => Number(assignedTeam?.id) === team.id
          );
          // Include teams that are owned by current user OR have enabled course sharing
          return (!isCurrentTeam && !isAlreadyAssigned && (team.owned_by_current_user || team.enable_course_sharing));
        });
        
        setAllTeams(filteredTeams);
      }
    } catch (error) {
      console.error("Error fetching teams for course sharing:", error);
      addToast("Failed to load teams", { appearance: "error" });
    } finally {
      setFetchingTeams(false);
    }
  }, [teamId, course, addToast]);

  useEffect(() => {
    if (dialogState) {
      fetchTeams();
    }
  }, [dialogState, fetchTeams]);

  const handleChange = (id) => {
    if (shareTeams.includes(id)) {
      setSharedTeams(shareTeams.filter(teamId => teamId !== id));
    } else {
      setSharedTeams([...shareTeams, id]);
    }
  };

  const checkSrc = (e) => {
    e.target.src = DefaultTeamAvatar;
  };

  const resetAndCloseModal = () => {
    setSharedTeams([]);
    setDialogState(false);
    setShowStatusError(false);
  };

  const assignCourse = async () => {
    setLoading(true);
    try {
      await CourseService.assignCourseToTeam({
        course_id: course.id,
        team_ids: shareTeams
      });
      
      addToast("Course assigned successfully", { appearance: "success" });

      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
      
      resetAndCloseModal();
    } catch (error) {
      console.error("Error assigning course:", error);
      addToast("Failed to assign course", { appearance: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignClick = () => {
    if (course?.status !== "published") {
      setShowStatusError(true);
      addToast("Cannot assign course until it is published", { appearance: "error" });
    } else {
      setShowStatusError(false);
      setDialogState(true);
    }
  };

  const isPublished = course?.status === "published";
  const assignButtonTooltip = isPublished ? "Assign course to teams" : "Course must be published before it can be assigned to teams";

  return (
    <>
      {renderButton ? (
        renderButton({ 
          onClick: handleAssignClick,
          disabled: !isPublished,
          title: assignButtonTooltip
        })
      ) : (
        <button 
          className="dropdown-item d-flex align-items-center py-2"
          onClick={handleAssignClick}
          style={{ 
            opacity: isPublished ? 1 : 0.6,
            cursor: isPublished ? "pointer" : "not-allowed"
          }}
          title={assignButtonTooltip}
        >
          <ShareIcon style={{ fill: "#ffa100" }} className="me-2 mw-20" />
          Assign
        </button>
      )}
      {showStatusError && (
        <div style={styles.statusErrorMessage}>
          Course must be published before it can be assigned to teams.
        </div>
      )}
      <Modal
        open={dialogState}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        center={true}
        onClose={resetAndCloseModal}
        showCloseIcon={false}
        classNames={{
          modalContainer: "modalContainer",
          modal: "shareModalPadding",
        }}
      >
        <div className="modal fade popup-modal">
          <div className="modal-content">
            <div className="modal-body">
              <div style={classes.dialogTitle}>
                <h5 style={classes.headingDailog}>Select Teams</h5>
                {!!allTeams && allTeams?.length > 0 && (
                  <div style={classes.rightButton}>
                    <Button
                      buttonStyle={styles.doneButton}
                      onClick={assignCourse}
                      type="button"
                      name={!loading ? "Done" : <Loader width={20} height={20} />}
                      disabled={shareTeams.length === 0 || loading}
                    />
                  </div>
                )}
              </div>
              <div style={classes.dialogContent}>
                <Scrollbar style={{ paddingRight: "10px" }}>
                  <div style={classes.listContainer}>
                    {fetchingTeams && (
                      <div style={classes.loaderClass}>
                        <Loader />
                      </div>
                    )}
                    {allTeams?.length === 0 && (
                      <div style={classes.noTeams}>
                        <p>No teams available to assign.</p>
                      </div>
                    )}
                    {allTeams?.map((team) => (
                      <div
                        onClick={() => handleChange(team.id)}
                        style={{
                          ...classes.listItem,
                          ...(shareTeams.includes(team.id) ? classes.selectedListItem : {})
                        }}
                        key={team.id}
                      >
                        <div style={classes.teamDetail}>
                          <img
                            onError={checkSrc}
                            alt={team.avatar}
                            style={classes.avatar}
                            src={team.avatar ?? DefaultTeamAvatar}
                          />
                          <span style={classes.teamName}>
                            {team.name}
                            {!team.owned_by_current_user && team.enable_course_sharing && (
                              <span 
                                style={{
                                  backgroundColor: '#ffa100', 
                                  color: 'white', 
                                  fontSize: '10px', 
                                  padding: '2px 6px', 
                                  borderRadius: '10px',
                                  marginLeft: '5px'
                                }}
                              >
                                External
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Scrollbar>
              </div>
              <div>
                <Button
                  type="button"
                  disabled={loading}
                  buttonClass="small-btn me-2"
                  name="Cancel"
                  onClick={resetAndCloseModal}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};